import React, { useEffect, useState, useRef } from 'react';
import './FarmGallery.css';

export default function FarmGallery() {
  const [images, setImages] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const [current, setCurrent] = useState(0);
  const sliderRef = useRef(null);

  useEffect(() => {
    fetch('/farm/farm-manifest.json')
      .then((r) => r.json())
      .then((imgs) => setImages(imgs))
      .catch(() => setImages([]));
  }, []);

  // scroll slider track (not the page) to current index
  useEffect(() => {
    if (!sliderRef.current || images.length === 0) return;
    const item = sliderRef.current.children[current];
    if (item) {
      sliderRef.current.scrollTo({
        left: item.offsetLeft - sliderRef.current.offsetLeft,
        behavior: 'smooth',
      });
    }
  }, [current, images]);

  const prev = () => setCurrent((i) => (i - 1 + images.length) % images.length);
  const next = () => setCurrent((i) => (i + 1) % images.length);

  // lightbox keyboard
  useEffect(() => {
    if (lightbox === null) return;
    const handler = (e) => {
      if (e.key === 'ArrowLeft')  setLightbox((i) => (i - 1 + images.length) % images.length);
      if (e.key === 'ArrowRight') setLightbox((i) => (i + 1) % images.length);
      if (e.key === 'Escape')     setLightbox(null);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightbox, images.length]);

  if (images.length === 0) return null;

  return (
    <section className="farm-gallery" id="gallery">
      <div className="farm-gallery-header">
        <h2>Our Farm</h2>
        <p>A glimpse into where your food is grown.</p>
      </div>

      {/* Slider */}
      <div className="slider-wrap">
        <button className="slider-btn slider-prev" onClick={prev} aria-label="Previous">
          &#8249;
        </button>

        <div className="slider-track" ref={sliderRef}>
          {images.map((file, idx) => (
            <div
              className={`slide ${idx === current ? 'active' : ''}`}
              key={file}
              onClick={() => setLightbox(idx)}
              role="button"
              tabIndex={0}
              aria-label={`View farm image ${idx + 1}`}
              onKeyDown={(e) => e.key === 'Enter' && setLightbox(idx)}
            >
              <img src={`/farm/${file}`} alt={`Farm ${idx + 1}`} />
              <div className="slide-overlay"><span>🔍</span></div>
            </div>
          ))}
        </div>

        <button className="slider-btn slider-next" onClick={next} aria-label="Next">
          &#8250;
        </button>
      </div>

      {/* Dots */}
      <div className="slider-dots">
        {images.map((_, idx) => (
          <button
            key={idx}
            className={`dot ${idx === current ? 'active' : ''}`}
            onClick={() => setCurrent(idx)}
            aria-label={`Go to image ${idx + 1}`}
          />
        ))}
      </div>

      {/* Lightbox */}
      {lightbox !== null && (
        <div className="lightbox" onClick={() => setLightbox(null)}>
          <button
            className="lb-close"
            onClick={() => setLightbox(null)}
            aria-label="Close"
          >✕</button>

          <button
            className="lb-arrow lb-prev"
            onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i - 1 + images.length) % images.length); }}
            aria-label="Previous"
          >&#8249;</button>

          <img
            src={`/farm/${images[lightbox]}`}
            alt={`Farm ${lightbox + 1}`}
            onClick={(e) => e.stopPropagation()}
          />

          <button
            className="lb-arrow lb-next"
            onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i + 1) % images.length); }}
            aria-label="Next"
          >&#8250;</button>

          <p className="lb-counter">{lightbox + 1} / {images.length}</p>
        </div>
      )}
    </section>
  );
}
